import {
  doc,
  collection,
  getDoc,
  setDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
  onSnapshot,
  query,
  Unsubscribe,
} from 'firebase/firestore';
import { getDb } from './config';
import { FirestoreUser, getCollectionPath } from './types';

// Interface matching Zuper API user structure
interface ZuperUser {
  user_uid: string;
  first_name: string;
  last_name: string;
  profile_picture: string | null;
  designation: string | null;
  is_glass_connected: boolean;
  is_active: boolean;
}

/**
 * Upsert a single user to Firestore
 * Only syncs user profile data, NOT status fields (is_online, is_glass_connected)
 * Status fields are only set on initial creation (default: false) and updated by external app
 */
export async function upsertUser(user: ZuperUser): Promise<void> {
  const db = getDb();
  const usersCollection = collection(db, getCollectionPath.users());
  const userRef = doc(usersCollection, user.user_uid);

  // Check if user already exists
  const existingUser = await getDoc(userRef);

  if (existingUser.exists()) {
    // User exists - only update profile data, NOT status fields
    await updateDoc(userRef, {
      first_name: user.first_name,
      last_name: user.last_name,
      profile_picture: user.profile_picture,
      designation: user.designation,
      updated_at: serverTimestamp(),
    });
  } else {
    // New user - set all fields with default status (offline, no glass, not on call)
    await setDoc(userRef, {
      user_uid: user.user_uid,
      first_name: user.first_name,
      last_name: user.last_name,
      profile_picture: user.profile_picture,
      designation: user.designation,
      is_glass_connected: false, // Default: not connected
      is_online: false, // Default: offline
      current_room_id: null, // Default: not on a call
      updated_at: serverTimestamp(),
    });
  }
}

/**
 * Batch upsert multiple users to Firestore
 * Only syncs user profile data, NOT status fields
 * Status fields default to false on initial creation
 */
export async function upsertUsers(users: ZuperUser[]): Promise<void> {
  const db = getDb();
  const BATCH_SIZE = 500;
  const usersCollection = collection(db, getCollectionPath.users());

  // First, get all existing users to determine which are new
  const existingUserIds = new Set<string>();
  for (const user of users) {
    const userRef = doc(usersCollection, user.user_uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      existingUserIds.add(user.user_uid);
    }
  }

  // Process in batches of 500 (Firestore limit)
  for (let i = 0; i < users.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = users.slice(i, i + BATCH_SIZE);

    for (const user of chunk) {
      const userRef = doc(usersCollection, user.user_uid);

      if (existingUserIds.has(user.user_uid)) {
        // Existing user - only update profile data
        batch.update(userRef, {
          first_name: user.first_name,
          last_name: user.last_name,
          profile_picture: user.profile_picture,
          designation: user.designation,
          updated_at: serverTimestamp(),
        });
      } else {
        // New user - set all fields with default status
        batch.set(userRef, {
          user_uid: user.user_uid,
          first_name: user.first_name,
          last_name: user.last_name,
          profile_picture: user.profile_picture,
          designation: user.designation,
          is_glass_connected: false, // Default: not connected
          is_online: false, // Default: offline
          current_room_id: null, // Default: not on a call
          updated_at: serverTimestamp(),
        });
      }
    }

    await batch.commit();
  }
}

/**
 * Get a user by their UID
 */
export async function getUserByUid(uid: string): Promise<FirestoreUser | null> {
  const db = getDb();
  const usersCollection = collection(db, getCollectionPath.users());
  const userRef = doc(usersCollection, uid);
  const userSnap = await getDoc(userRef);

  if (userSnap.exists()) {
    return userSnap.data() as FirestoreUser;
  }
  return null;
}

/**
 * Update a user's online status (called by external Glass app)
 * Uses setDoc with merge to create doc if it doesn't exist
 */
export async function updateUserOnlineStatus(uid: string, isOnline: boolean): Promise<void> {
  const db = getDb();
  const usersCollection = collection(db, getCollectionPath.users());
  const userRef = doc(usersCollection, uid);

  // Use setDoc with merge to handle case where user doc doesn't exist yet
  await setDoc(userRef, {
    user_uid: uid,
    is_online: isOnline,
    updated_at: serverTimestamp(),
  }, { merge: true });
}

/**
 * Update a user's glass connected status (called by external Glass app)
 * Uses setDoc with merge to create doc if it doesn't exist
 */
export async function updateUserGlassStatus(uid: string, isGlassConnected: boolean): Promise<void> {
  const db = getDb();
  const usersCollection = collection(db, getCollectionPath.users());
  const userRef = doc(usersCollection, uid);

  await setDoc(userRef, {
    user_uid: uid,
    is_glass_connected: isGlassConnected,
    updated_at: serverTimestamp(),
  }, { merge: true });
}

/**
 * Update a user's current room (for busy status)
 * Set roomId when joining a call, null when leaving
 * Uses setDoc with merge to create doc if it doesn't exist
 */
export async function updateUserCurrentRoom(uid: string, roomId: string | null): Promise<void> {
  const db = getDb();
  const usersCollection = collection(db, getCollectionPath.users());
  const userRef = doc(usersCollection, uid);

  await setDoc(userRef, {
    user_uid: uid,
    current_room_id: roomId,
    updated_at: serverTimestamp(),
  }, { merge: true });
}

/**
 * Subscribe to real-time updates for all users
 * Returns an unsubscribe function
 */
export function subscribeToUsers(
  onUsersUpdate: (users: FirestoreUser[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const db = getDb();
  const usersCollection = collection(db, getCollectionPath.users());
  const q = query(usersCollection);

  return onSnapshot(
    q,
    (snapshot) => {
      const users: FirestoreUser[] = [];
      snapshot.forEach((doc) => {
        users.push(doc.data() as FirestoreUser);
      });
      onUsersUpdate(users);
    },
    (error) => {
      console.error('Error listening to users:', error);
      onError?.(error);
    }
  );
}
